import type {
  FieldValue,
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import type { FieldValuesSetter } from 'features/libra/ticket/fieldOrigin'
import { z } from 'zod'

import type { ArrayTableSectionSchema } from './arrayTableSection/arrayTableSectionTypes'
import {
  addComputedSectionSnapshotIssue,
  type ComputedSectionSchema,
} from './computedSection/computedSectionTypes'
import type { DocumentAttachmentsSectionSchema } from './documentAttachmentsSection/documentAttachmentsTypes'
import type { FlowchartSectionSchema } from './flowchartSection/flowchartTypes'
import type { FormSectionSchema } from './formSection/formSectionTypes'
import { type SectionConfigMap, sectionRegistry } from './SectionRegistry'
import type { ZonesSectionSchema } from './zonesSection/zonesSectionTypes'

export const SectionSchema = z
  .union(Object.values(sectionRegistry).map(({ schema }) => schema))
  .superRefine((section, ctx) => addComputedSectionSnapshotIssue(section, ctx))

export type FormSection = z.infer<typeof FormSectionSchema>
export type FlowchartSection = z.infer<typeof FlowchartSectionSchema>
export type ComputedSection = z.infer<typeof ComputedSectionSchema>
export type ArrayTableSection = z.infer<typeof ArrayTableSectionSchema>
export type ZonesSection = z.infer<typeof ZonesSectionSchema>
export type DocumentAttachmentsSection = z.infer<
  typeof DocumentAttachmentsSectionSchema
>
export type Section = SectionConfigMap[keyof SectionConfigMap]

export type SavePartialField = (
  fieldId: string,
  prepare: (latest: FieldValue | undefined) => FieldValue,
  reconcile: (
    current: FieldValue | undefined,
    applied: FieldValue
  ) => FieldValue
) => Promise<void>

export interface SectionRenderProps<Config> {
  config: Config
  stepId: string
  ontologyFields: Record<string, OntologyField>
  fieldValues: FieldValues
  initialFieldValues: FieldValues
  setFieldValues: FieldValuesSetter | undefined
  savePartialField?: SavePartialField
  /** Cancels transient drawing intents when the owning form is reset. */
  drawingResetKey?: number
}
